<template>
  <div v-if="isVisible" class="settings-modal-overlay" @click="handleOverlayClick">
    <div class="settings-modal" @click.stop>
      <div class="settings-header">
        <h2>{{ t('settings.title') }}</h2>
        <button class="close-button" @click="$emit('close')">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
      
      <div class="settings-content">
        <!-- 语言设置 -->
        <div class="setting-section">
          <h3>{{ t('settings.languageSettings') }}</h3>
          <select 
            class="language-select"
            :value="currentLanguage"
            @change="handleLanguageChange"
          >
            <option value="zh-CN">中文</option>
            <option value="en-US">English</option>
            <option value="ja-JP">日本語</option>
            <option value="es-ES">Español</option>
            <option value="fr-FR">Français</option>
            <option value="de-DE">Deutsch</option>
            <option value="ko-KR">한국어</option>
            <option value="pt-BR">Português</option>
            <option value="ru-RU">Русский</option>
            <option value="it-IT">Italiano</option>
          </select>
        </div>
        
        <!-- 显示设置 -->
        <div class="setting-section">
          <h3>{{ t('settings.displaySettings') }}</h3>
          <div class="setting-item">
            <label class="setting-label">
              <input 
                type="checkbox" 
                :checked="!isBackgroundVisible"
                @change="$emit('toggle-background')"
                class="setting-checkbox"
              >
              <span class="checkbox-custom"></span>
              {{ t('settings.hideBackground') }}
            </label>
          </div>
        </div>
        
        <!-- 音乐设置 -->
        <div class="setting-section">
          <h3>{{ t('settings.musicSettings') }}</h3>
          
          <!-- 音量控制 -->
          <div class="volume-section">
            <h4>{{ t('settings.volumeControl') }}</h4>
            <div class="volume-control">
              <button class="volume-button" @click="$emit('toggle-mute')">
                <svg v-if="!isMuted && volume > 0" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <polygon points="11,5 6,9 2,9 2,15 6,15 11,19" fill="currentColor"/>
                  <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" stroke="currentColor" stroke-width="2"/>
                </svg>
                <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <polygon points="11,5 6,9 2,9 2,15 6,15 11,19" fill="currentColor"/>
                  <line x1="23" y1="9" x2="17" y2="15" stroke="currentColor" stroke-width="2"/>
                  <line x1="17" y1="9" x2="23" y2="15" stroke="currentColor" stroke-width="2"/>
                </svg>
              </button>
              <div class="volume-slider-container">
                <input 
                  type="range" 
                  class="volume-slider" 
                  min="0" 
                  max="100" 
                  :value="volume"
                  @input="handleVolumeChange"
                  @change="$emit('set-volume', $event.target.value)"
                >
                <span class="volume-value">{{ volume }}%</span>
              </div>
            </div>
            
            <!-- 音量预设 -->
            <div class="volume-presets">
              <button 
                v-for="preset in volumePresets" 
                :key="preset.value"
                class="volume-preset-btn"
                :class="{ active: Math.abs(volume - preset.value) <= 5 }"
                @click="$emit('set-volume', preset.value)"
                :title="`${t('settings.setVolumeTo')} ${preset.value}%`"
              >
                {{ t(`settings.${preset.key}`) }}
              </button>
            </div>
          </div>
          <div class="music-upload-section">
            <input 
              ref="fileInput"
              type="file" 
              accept="audio/*" 
              @change="handleFileUpload"
              style="display: none"
              multiple
            >
            <button class="upload-button" @click="$refs.fileInput.click()">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7,10 12,15 17,10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              {{ t('settings.uploadMusic') }}
            </button>
          </div>
          
          <!-- 上传音乐列表 -->
          <div v-if="uploadedTracks.length > 0" class="uploaded-tracks">
            <h4>{{ t('settings.uploadedMusic') }}</h4>
            <div class="track-list">
              <div 
                v-for="(track, index) in uploadedTracks" 
                :key="index"
                class="track-item"
              >
                <div class="track-info">
                  <span class="track-title">{{ track.title }}</span>
                  <span class="track-artist">{{ track.artist }}</span>
                </div>
                <button 
                  class="remove-button"
                  @click="removeTrack(index)"
                  :title="t('settings.delete')"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <polyline points="3,6 5,6 21,6"/>
                    <path d="M19,6v14a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6m3,0V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2v2"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
          
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useI18n } from '@/i18n'

export default {
  name: 'SettingsModal',
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    uploadedTracks: {
      type: Array,
      default: () => []
    },
    isBackgroundVisible: {
      type: Boolean,
      default: true
    },
    volume: {
      type: Number,
      default: 30
    },
    isMuted: {
      type: Boolean,
      default: false
    },
  },
  emits: ['close', 'upload-track', 'remove-track', 'toggle-background', 'set-volume', 'toggle-mute'],
  setup(props, { emit }) {
    const { currentLanguage, setLanguage, t } = useI18n()
    
    // 音量预设
    const volumePresets = [
      { key: 'low', value: 20 },
      { key: 'medium', value: 50 },
      { key: 'high', value: 80 }
    ]
    
    // 实时音量调整处理
    const handleVolumeChange = (event) => {
      emit('set-volume', event.target.value)
    }
    
    const handleLanguageChange = (event) => {
      setLanguage(event.target.value)
    }
    
    const handleOverlayClick = () => {
      emit('close')
    }
    
    const handleFileUpload = (event) => {
      const files = Array.from(event.target.files)
      files.forEach(file => {
        if (file && file.type.startsWith('audio/')) {
          emit('upload-track', file)
        }
      })
      // 清空文件输入，允许再次选择同一文件
      event.target.value = ''
    }
    
    const removeTrack = (index) => {
      emit('remove-track', index)
    }
    
    return {
      currentLanguage,
      volumePresets,
      handleLanguageChange,
      handleOverlayClick,
      handleFileUpload,
      removeTrack,
      handleVolumeChange,
      t
    }
  }
}
</script>

<style scoped>
.settings-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
}

/* 弹窗全屏确保 - #app已经突破safe area，这里只需保证完全覆盖 */
@supports (env(safe-area-inset-top)) {
  .settings-modal-overlay {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    width: 100% !important;
    height: 100% !important;
  }
}

.settings-modal {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
  color: white;
}

.settings-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 25px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.settings-header h2 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 500;
}

.close-button {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-button:hover {
  color: white;
  background: rgba(255, 255, 255, 0.1);
}

.close-button svg {
  width: 20px;
  height: 20px;
}

.settings-content {
  padding: 25px;
}

.setting-section {
  margin-bottom: 30px;
}

.setting-section:last-child {
  margin-bottom: 0;
}

.setting-section h3 {
  margin: 0 0 15px 0;
  font-size: 1.1rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.language-select {
  width: 100%;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 12px 16px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  outline: none;
  appearance: none;
}

.language-select:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.4);
}

.language-select:focus {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.6);
  box-shadow: 0 0 15px rgba(255, 255, 255, 0.3);
}

.language-select option {
  background: rgba(30, 58, 138, 0.95);
  color: white;
  padding: 8px;
}

.music-upload-section {
  margin-bottom: 20px;
}

.upload-button {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 12px 20px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  justify-content: center;
}

.upload-button:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.4);
  color: white;
}

.upload-button svg {
  width: 20px;
  height: 20px;
}

.uploaded-tracks h4 {
  margin: 0 0 12px 0;
  font-size: 1rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
}

.track-list {
  max-height: 200px;
  overflow-y: auto;
}

.track-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  margin-bottom: 8px;
  transition: all 0.3s ease;
}

.track-item:hover {
  background: rgba(255, 255, 255, 0.1);
}

.track-info {
  flex: 1;
  min-width: 0;
}

.track-title {
  display: block;
  font-size: 0.9rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.track-artist {
  display: block;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 2px;
}

.remove-button {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 6px;
  border-radius: 50%;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 10px;
}

.remove-button:hover {
  color: #ff6b6b;
  background: rgba(255, 107, 107, 0.1);
}

.remove-button svg {
  width: 16px;
  height: 16px;
}

/* 设置项样式 */
.setting-item {
  margin-bottom: 15px;
}

.setting-label {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  transition: color 0.3s ease;
}

.setting-label:hover {
  color: white;
}

.setting-checkbox {
  display: none;
}

.checkbox-custom {
  position: relative;
  width: 20px;
  height: 20px;
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.setting-checkbox:checked + .checkbox-custom {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.8);
}

.setting-checkbox:checked + .checkbox-custom::after {
  content: '';
  position: absolute;
  left: 5px;
  top: 2px;
  width: 6px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

/* 音量控制样式 */
.volume-section {
  margin-bottom: 20px;
}

.volume-section h4 {
  margin: 0 0 12px 0;
  font-size: 1rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
}

.volume-control {
  display: flex;
  align-items: center;
  gap: 15px;
}

.volume-button {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 8px;
  color: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 40px;
}

.volume-button:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.4);
  color: white;
}

.volume-button svg {
  width: 20px;
  height: 20px;
}

.volume-slider-container {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
}

.volume-slider {
  flex: 1;
  height: 6px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
  outline: none;
  appearance: none;
  cursor: pointer;
  transition: all 0.3s ease;
}

.volume-slider::-webkit-slider-thumb {
  appearance: none;
  width: 18px;
  height: 18px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}

.volume-slider::-webkit-slider-thumb:hover {
  background: white;
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.volume-slider::-moz-range-thumb {
  width: 18px;
  height: 18px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  cursor: pointer;
  border: none;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}

.volume-slider::-moz-range-thumb:hover {
  background: white;
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.volume-slider:hover {
  background: rgba(255, 255, 255, 0.3);
}

.volume-value {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
  min-width: 40px;
  text-align: right;
  font-weight: 500;
}

.volume-presets {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}

.volume-preset-btn {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  padding: 6px 12px;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.85rem;
  font-weight: 500;
  min-width: 36px;
}

.volume-preset-btn:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.4);
  color: white;
  transform: translateY(-1px);
}

.volume-preset-btn.active {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.6);
  color: white;
  box-shadow: 0 2px 8px rgba(255, 255, 255, 0.1);
}


/* 移动端响应式 */
@media (max-width: 768px) {
  .settings-modal {
    width: 95%;
    max-height: 85vh;
  }
  
  .settings-header {
    padding: 15px 20px;
  }
  
  .settings-header h2 {
    font-size: 1.3rem;
  }
  
  .settings-content {
    padding: 20px;
  }
  
  .volume-control {
    gap: 12px;
  }
  
  .volume-button {
    min-width: 36px;
    height: 36px;
  }
  
  .volume-button svg {
    width: 18px;
    height: 18px;
  }
  
  .volume-slider-container {
    gap: 10px;
  }
  
  .volume-value {
    font-size: 0.85rem;
    min-width: 35px;
  }
}

/* iOS Safari按钮显示修复 - 边框+图标样式 */
@supports (-webkit-touch-callout: none) {
  .language-select {
    background: transparent !important;
    border: 2px solid rgba(255, 255, 255, 0.6) !important;
    backdrop-filter: none !important;
    color: rgba(255, 255, 255, 0.8) !important;
    -webkit-appearance: none !important;
    appearance: none !important;
  }
  
  .language-select:hover {
    background: transparent !important;
    border-color: rgba(255, 255, 255, 0.9) !important;
    color: rgba(255, 255, 255, 1) !important;
  }
  
  .language-select:focus {
    background: transparent !important;
    border-color: rgba(255, 255, 255, 1) !important;
    color: white !important;
    box-shadow: none !important;
  }
  
  .volume-button {
    background: transparent !important;
    border: 2px solid rgba(255, 255, 255, 0.6) !important;
    backdrop-filter: none !important;
  }
  
  .volume-button:hover {
    background: transparent !important;
    border-color: rgba(255, 255, 255, 0.9) !important;
  }
  
  .volume-button svg {
    fill: none !important;
    stroke: rgba(255, 255, 255, 0.8) !important;
    stroke-width: 1.5 !important;
  }
  
  .volume-button:hover svg {
    stroke: rgba(255, 255, 255, 1) !important;
  }
  
  .volume-button svg path,
  .volume-button svg polygon,
  .volume-button svg line {
    fill: rgba(255, 255, 255, 0.8) !important;
    stroke: rgba(255, 255, 255, 0.8) !important;
  }
  
  .volume-button:hover svg path,
  .volume-button:hover svg polygon,
  .volume-button:hover svg line {
    fill: rgba(255, 255, 255, 1) !important;
    stroke: rgba(255, 255, 255, 1) !important;
  }
  
  .upload-button {
    background: transparent !important;
    border: 2px solid rgba(255, 255, 255, 0.6) !important;
    backdrop-filter: none !important;
    color: rgba(255, 255, 255, 0.8) !important;
  }
  
  .upload-button:hover {
    background: transparent !important;
    border-color: rgba(255, 255, 255, 0.9) !important;
    color: rgba(255, 255, 255, 1) !important;
  }
  
  .upload-button svg {
    fill: none !important;
    stroke: rgba(255, 255, 255, 0.8) !important;
    stroke-width: 1.5 !important;
  }
  
  .upload-button:hover svg {
    stroke: rgba(255, 255, 255, 1) !important;
  }
  
  .volume-preset-btn {
    background: transparent !important;
    border: 2px solid rgba(255, 255, 255, 0.6) !important;
    backdrop-filter: none !important;
    color: rgba(255, 255, 255, 0.8) !important;
  }
  
  .volume-preset-btn:hover {
    background: transparent !important;
    border-color: rgba(255, 255, 255, 0.9) !important;
    color: rgba(255, 255, 255, 1) !important;
  }
  
  .volume-preset-btn.active {
    background: transparent !important;
    border-color: rgba(255, 255, 255, 1) !important;
    color: white !important;
    box-shadow: none !important;
  }
  
  .close-button {
    background: transparent !important;
    border: 2px solid rgba(255, 255, 255, 0.6) !important;
  }
  
  .close-button:hover {
    background: transparent !important;
    border-color: rgba(255, 255, 255, 0.9) !important;
  }
  
  .close-button svg {
    stroke: rgba(255, 255, 255, 0.8) !important;
    stroke-width: 1.5 !important;
  }
  
  .close-button:hover svg {
    stroke: rgba(255, 255, 255, 1) !important;
  }
  
  .remove-button {
    background: transparent !important;
    border: 2px solid rgba(255, 255, 255, 0.6) !important;
  }
  
  .remove-button:hover {
    background: transparent !important;
    border-color: rgba(255, 107, 107, 0.9) !important;
  }
  
  .remove-button svg {
    stroke: rgba(255, 255, 255, 0.8) !important;
    stroke-width: 1.5 !important;
  }
  
  .remove-button:hover svg {
    stroke: rgba(255, 107, 107, 1) !important;
  }
}

@media (max-width: 480px) {
  .settings-modal {
    width: 98%;
    max-height: 90vh;
  }
  
  .settings-header {
    padding: 12px 15px;
  }
  
  .settings-content {
    padding: 15px;
  }
  
  .volume-control {
    gap: 10px;
  }
  
  .volume-button {
    min-width: 32px;
    height: 32px;
  }
  
  .volume-button svg {
    width: 16px;
    height: 16px;
  }
  
  .volume-slider-container {
    gap: 8px;
  }
  
  .volume-value {
    font-size: 0.8rem;
    min-width: 30px;
  }
}
</style>